import type { SearchableSelectOption } from '@/components/ui/SearchableSelect'
import type { RevenuePaymentLogsListParams, RevenuePaymentLogsSortKey } from '../../types/pages'

/** Applied only when contract start dates are set without payment types (Angular `leasedateUpdated`). */
export const DEFAULT_CONTRACT_TYPES = ['CONTRACT_INVOICE', 'CONTRACT']

/** Angular `defaultFilter` + empty form fields after Reset / initial load (no default type[]). */
export const DEFAULT_PARAMS: RevenuePaymentLogsListParams = {
  page: 1,
  perPage: 15,
  sortBy: 'desc',
  orderBy: 'created_at',
  q: '',
  company_id: '',
  property_id: '',
  fromDt: '',
  toDt: '',
  contract_start_fromDt: '',
  contract_start_toDt: '',
  funding_fromDt: '',
  funding_toDt: '',
  type: [],
  source: '',
  status: [],
  gateway_status: [],
  special_note: '',
}

export function createEmptyRevenuePaymentLogsParams(
  overrides: Partial<RevenuePaymentLogsListParams> = {},
): RevenuePaymentLogsListParams {
  return {
    ...DEFAULT_PARAMS,
    type: [],
    status: [],
    gateway_status: [],
    ...overrides,
  }
}

/**
 * Normalize drawer draft before list fetch — matches Angular `getFilterValue` + `leasedateUpdated`.
 * Empty multi-selects are omitted by the query builder (not forced).
 */
export function normalizeRevenuePaymentLogsFilters(
  draft: RevenuePaymentLogsListParams,
): RevenuePaymentLogsListParams {
  const next: RevenuePaymentLogsListParams = {
    ...draft,
    page: 1,
    q: draft.q.trim(),
    source: draft.source.trim(),
    special_note: draft.special_note.trim(),
    type: Array.isArray(draft.type) ? [...draft.type] : [],
    status: Array.isArray(draft.status) ? [...draft.status] : [],
    gateway_status: Array.isArray(draft.gateway_status)
      ? [...draft.gateway_status]
      : [],
  }
  // Angular: contract start selected with no type → default CONTRACT_INVOICE + CONTRACT
  if (
    next.type.length === 0 &&
    (next.contract_start_fromDt || next.contract_start_toDt)
  ) {
    next.type = [...DEFAULT_CONTRACT_TYPES]
  }
  return next
}

export const PAYMENT_TYPE_OPTIONS: SearchableSelectOption[] = [
  { value: 'APPLICATION_FEE', label: 'Application Payment' },
  { value: 'APPLICATION_FEE_REFUND', label: 'Application Refund' },
  { value: 'CONTRACT', label: 'Contract Payment' },
  { value: 'CONTRACT_INVOICE', label: 'Contract Invoice' },
  { value: 'CONTRACT_REFUND_FULL', label: 'Contract Refund - Full' },
  {
    value: 'CONTRACT_REFUND_PARTIAL',
    label: 'Contract Refund - Partial',
  },
  { value: 'DEFAULT_INVOICE', label: 'Default Recovery' },
]

export const PAYMENT_STATUS_OPTIONS: SearchableSelectOption[] = [
  { value: 'ACH_Check_Pending', label: 'ACH Check Pending' },
  { value: 'Approved', label: 'Approved' },
  { value: 'AuthorizationPending', label: 'Authorization Pending' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Processing', label: 'Processing' },
  { value: 'Rejected', label: 'Rejected' },
]

export const PAYMENT_TYPE_LABELS = Object.fromEntries(
  PAYMENT_TYPE_OPTIONS.map((o) => [o.value, o.label]),
) as Record<string, string>

export const SORT_COLUMNS: { key: RevenuePaymentLogsSortKey; label: string }[] = [
  { key: 'payment_log_id', label: 'Log Id' },
  { key: 'company_name', label: 'Company' },
  { key: 'property_name', label: 'Property' },
  { key: 'applicant_id', label: 'Applicant ID' },
  { key: 'application_id', label: 'Application ID' },
  { key: 'first_name', label: 'Name' },
  { key: 'special_note', label: 'Special Note' },
  { key: 'payment_type', label: 'Payment Type' },
  { key: 'payment_source', label: 'Payment Source' },
  { key: 'auth_code', label: 'Auth Code' },
  { key: 'payment_amount', label: 'Amount' },
  { key: 'surcharge_fee', label: 'Service Fee' },
  { key: 'payment_amount', label: 'Total Amount' },
  { key: 'payment_status', label: 'Status' },
  { key: 'gateway_status', label: 'Gateway Status' },
  { key: 'batch_id', label: 'Batch ID' },
  { key: 'funding_batch_id', label: 'Funding Batch ID' },
  { key: 'funding_master_id', label: 'Funding Master ID' },
  { key: 'funding_date', label: 'Funding Date' },
  { key: 'lease_start_date', label: 'Contract Start' },
  { key: 'created_at', label: 'Created Time' },
]
